import type {
  ClientCapabilities,
  Feedback,
  NextChannelRequest,
  PlaybackEvent,
  PresenceRequest,
  PresenceResponse
} from "@serendiptv/contracts";
import { ChatResponseSchema, PresenceResponseSchema } from "@serendiptv/contracts";
import type { GuideResponse, LiveChannelResponse, SelectionResponse, StationSummary } from "../types";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
export const REQUEST_TIMEOUT_MS = 8_000;

export type AccessStatus = { gate_enabled: boolean; unlocked: boolean };

export class AccessError extends Error {
  constructor(public status: number, public retryAfterSeconds?: number) {
    super(`Access request failed (${status})`);
    this.name = "AccessError";
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`SerendipTV service returned ${response.status}${body ? `: ${body.slice(0, 160)}` : ""}`);
  }
  return response.json() as Promise<T>;
}

/** Bound both the connection and body read, while allowing tuning to cancel immediately. */
async function requestWithDeadline<T>(url: string, init: RequestInit, read: (response: Response) => Promise<T>): Promise<T> {
  const callerSignal = init.signal;
  const abortError = () => new DOMException("SerendipTV request was canceled", "AbortError");
  if (callerSignal?.aborted) throw abortError();

  const controller = new AbortController();
  let cancel!: (reason: DOMException) => void;
  const cancellation = new Promise<never>((_resolve, reject) => {
    cancel = (reason) => {
      // Settle our cancellation first: older fetch implementations can discard
      // AbortSignal.reason and report AbortError even when the deadline expires.
      reject(reason);
      controller.abort(reason);
    };
  });
  const onAbort = () => cancel(abortError());
  callerSignal?.addEventListener("abort", onAbort, { once: true });
  const timeout = setTimeout(() => cancel(new DOMException("SerendipTV request timed out", "TimeoutError")), REQUEST_TIMEOUT_MS);
  try {
    return await Promise.race([
      fetch(url, { ...init, signal: controller.signal }).then(read),
      cancellation
    ]);
  } finally {
    clearTimeout(timeout);
    callerSignal?.removeEventListener("abort", onAbort);
  }
}

export interface NextSelectionInput {
  sessionId: string;
  viewerId: string;
  currentItemId: string | null;
  currentChannel: number;
  direction: "up" | "down";
  recentItemIds: string[];
  capabilities: ClientCapabilities;
  mode?: "rando";
  replayItemId?: string;
  replayOffsetSeconds?: number;
}

export function nextSelectionRequest(input: NextSelectionInput): NextChannelRequest {
  return {
    mode: input.mode,
    replay_item_id: input.replayItemId,
    replay_offset_seconds: input.replayOffsetSeconds,
    session_id: input.sessionId,
    viewer_id: input.viewerId,
    current_item_id: input.currentItemId,
    current_channel: input.currentChannel,
    direction: input.direction,
    recent_item_ids: input.recentItemIds.slice(-24),
    client_capabilities: input.capabilities
  };
}

export async function fetchAccessStatus(signal?: AbortSignal): Promise<AccessStatus> {
  return requestWithDeadline(`${API_BASE}/v1/access/status`, {
    headers: { accept: "application/json" }, credentials: "same-origin", signal
  }, parseResponse<AccessStatus>);
}

export async function unlockAccess(combination: readonly [number, number, number]): Promise<AccessStatus> {
  const response = await fetch(`${API_BASE}/v1/access/unlock`, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ combination }),
    credentials: "same-origin"
  });
  if (!response.ok) {
    const retry = response.headers.get("retry-after");
    const seconds = retry && /^\d+$/.test(retry) ? Number(retry)
      : retry ? Math.ceil((Date.parse(retry) - Date.now()) / 1000) : undefined;
    throw new AccessError(response.status, seconds !== undefined && Number.isFinite(seconds) ? Math.max(1, seconds) : undefined);
  }
  return parseResponse<AccessStatus>(response);
}

export async function fetchNextSelection(
  input: NextSelectionInput,
  signal?: AbortSignal
): Promise<SelectionResponse> {
  return requestWithDeadline(`${API_BASE}/v1/channels/next`, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(nextSelectionRequest(input)),
    signal
  }, parseResponse<SelectionResponse>);
}

export async function fetchStations(signal?: AbortSignal): Promise<StationSummary[]> {
  const body = await requestWithDeadline(`${API_BASE}/v1/stations`, {
    headers: { accept: "application/json" }, credentials: "same-origin", signal
  }, parseResponse<{ stations: StationSummary[] }>);
  return body.stations;
}

export async function fetchLiveChannel(channel: number, signal?: AbortSignal): Promise<LiveChannelResponse> {
  return requestWithDeadline(`${API_BASE}/v1/channels/${encodeURIComponent(channel)}/now`, {
    headers: { accept: "application/json" }, credentials: "same-origin", signal
  }, parseResponse<LiveChannelResponse>);
}

export async function fetchGuide(from: Date, to: Date, signal?: AbortSignal): Promise<GuideResponse> {
  const query = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
  return requestWithDeadline(`${API_BASE}/v1/guide?${query}`, {
    headers: { accept: "application/json" }, credentials: "same-origin", signal
  }, parseResponse<GuideResponse>);
}

export async function fetchItemDetails(itemId: string, signal?: AbortSignal): Promise<SelectionResponse["item"]> {
  return requestWithDeadline(`${API_BASE}/v1/items/${encodeURIComponent(itemId)}`, {
    headers: { accept: "application/json" },
    signal
  }, parseResponse<SelectionResponse["item"]>);
}

export async function postPlaybackEvent(event: PlaybackEvent, keepalive = false): Promise<void> {
  try {
    await fetch(`${API_BASE}/v1/events/playback`, {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(event),
      keepalive
    });
  } catch {
    // Telemetry is best effort and can never interrupt channel surfing.
  }
}

export function sendPlaybackBeacon(event: PlaybackEvent): boolean {
  if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") return false;

  try {
    const payload = new Blob([JSON.stringify(event)], { type: "application/json" });
    return navigator.sendBeacon(`${API_BASE}/v1/events/playback`, payload);
  } catch {
    return false;
  }
}

export async function postFeedback(feedback: Feedback, signal?: AbortSignal): Promise<void> {
  return requestWithDeadline(`${API_BASE}/v1/feedback`, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(feedback),
    signal
  }, async (response) => {
    if (!response.ok) await parseResponse<unknown>(response);
    // Feedback succeeds on 204 as well as other 2xx responses; no body is required.
  });
}

export async function postPresence(input: PresenceRequest, signal?: AbortSignal, keepalive = false): Promise<PresenceResponse> {
  const response = await fetch(`${API_BASE}/v1/presence`, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(input),
    signal,
    keepalive
  });
  return PresenceResponseSchema.parse(await parseResponse<unknown>(response));
}

export { API_BASE };

export async function getStationChat(channel: number, signal: AbortSignal) {
  const response = await fetch(`${API_BASE}/v1/chat/${channel}`, { credentials: "include", signal, cache: "no-store" });
  return ChatResponseSchema.parse(await parseResponse<unknown>(response));
}

export async function sendStationChat(channel: number, id: string, text: string, signal: AbortSignal) {
  const response = await fetch(`${API_BASE}/v1/chat/${channel}`, {
    method: "POST", credentials: "include", signal,
    headers: { "content-type": "application/json" }, body: JSON.stringify({ id, text })
  });
  if (!response.ok) throw new Error(response.status === 429 ? "Please wait a few seconds before sending again." : "Message not sent. Please try again.");
}
